import { useEffect, useState, useCallback, useRef } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { knowledgeService, knowledgeDocService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, Download, Trash2, Send, CheckCircle2, XCircle, Archive, FileText, UploadCloud,
} from "lucide-react";
import { VISIBILITIES, STATUS_COLOR, VISIBILITY_COLOR } from "./constants";

const ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp";
const MAX_MB = 25;

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

function bytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

export default function DocumentsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", visibility: "Internal", categoryId: "", tenantId: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await knowledgeDocService.list({ size: 200 }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);
  useEffect(() => {
    const tid = isSuper ? form.tenantId : undefined;
    if (isSuper && !tid) { setCategories([]); return; }
    knowledgeService.listCategories({ tenantId: tid }).then((d) => setCategories(d.items || [])).catch(() => {});
  }, [isSuper, form.tenantId]);

  const canUpload = hasPermission("KNOWLEDGE.UPLOAD");
  const canEdit = hasPermission("KNOWLEDGE.EDIT");
  const canApprove = hasPermission("KNOWLEDGE.APPROVE");
  const canPublish = hasPermission("KNOWLEDGE.PUBLISH");
  const canDelete = hasPermission("KNOWLEDGE.DELETE");

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`File exceeds ${MAX_MB}MB`); return; }
    setFile(f);
    if (!form.title) setForm((p) => ({ ...p, title: f.name.replace(/\.[^.]+$/, "") }));
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    if (!form.title.trim()) return toast.error("Title is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("description", form.description || "");
      fd.append("categoryId", form.categoryId || "");
      fd.append("visibility", form.visibility);
      if (isSuper) fd.append("tenantId", form.tenantId || "");
      await knowledgeDocService.upload(fd);
      toast.success("Document uploaded");
      setOpen(false); setFile(null);
      setForm({ title: "", description: "", visibility: "Internal", categoryId: "", tenantId: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const download = async (r) => {
    try {
      const res = await knowledgeDocService.download(r.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = r.fileName || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const remove = (r) => { if (window.confirm(`Delete "${r.title}"?`)) act(() => knowledgeDocService.remove(r.id), "Deleted"); };

  return (
    <div data-testid="documents-page">
      <PageHeader title="Policy Documents" subtitle="Secure, tenant-isolated document store with approval & versioning"
        actions={canUpload && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="document-upload-btn">
            <Plus className="h-4 w-4" /> Upload Document
          </button>
        )}
      />
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-10 col-span-full text-center">No documents yet</p>}
          {rows.map((r) => (
            <div key={r.id} className="bg-card rounded-xl border border-border p-4 shadow-sm flex flex-col" data-testid={`document-card-${r.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.fileName} · {bytes(r.size)} · v{r.currentVersion ?? 1}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Badge color={STATUS_COLOR[r.status]}>{r.status}</Badge>
                <Badge color={VISIBILITY_COLOR[r.visibility]}>{r.visibility}</Badge>
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-1 pt-2 border-t border-border">
                <button onClick={() => download(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Download" data-testid={`document-download-${r.id}`}><Download className="h-4 w-4" /></button>
                {canEdit && ["Draft", "Rejected"].includes(r.status) && <button onClick={() => act(() => knowledgeDocService.action(r.id, "submit"), "Submitted")} disabled={busy} className="p-1.5 rounded-md hover:bg-muted text-primary" title="Submit for review" data-testid={`document-submit-${r.id}`}><Send className="h-4 w-4" /></button>}
                {canApprove && r.status === "In Review" && (
                  <>
                    <button onClick={() => act(() => knowledgeDocService.action(r.id, "approve"), "Approved")} disabled={busy} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-600" title="Approve & publish" data-testid={`document-approve-${r.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                    <button onClick={() => { const x = window.prompt("Rejection reason?"); if (x !== null) act(() => knowledgeDocService.action(r.id, "reject", { reason: x }), "Rejected"); }} disabled={busy} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-600" title="Reject" data-testid={`document-reject-${r.id}`}><XCircle className="h-4 w-4" /></button>
                  </>
                )}
                {canPublish && r.status === "Published" && <button onClick={() => act(() => knowledgeDocService.action(r.id, "archive"), "Archived")} disabled={busy} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Archive" data-testid={`document-archive-${r.id}`}><Archive className="h-4 w-4" /></button>}
                {canDelete && <button onClick={() => remove(r)} className="ml-auto p-1.5 rounded-md hover:bg-destructive/10 text-destructive" title="Delete" data-testid={`document-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="document-dialog">
            <h3 className="text-lg font-bold mb-4">Upload Document</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value, categoryId: "" })} className="ciq-input" data-testid="document-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-border rounded-lg p-5 text-center cursor-pointer hover:border-primary/60 transition-colors" data-testid="document-dropzone">
                <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground mb-1.5" />
                <p className="text-sm text-foreground">{file ? file.name : "Click to choose a file"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{file ? bytes(file.size) : `Max ${MAX_MB}MB · PDF, Office, images`}</p>
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={pickFile} data-testid="document-file-input" />
              </div>
              <Field label="Title *"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" data-testid="document-title" /></Field>
              <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} data-testid="document-description" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Visibility"><select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="ciq-input" data-testid="document-visibility">{VISIBILITIES.map((v) => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Category"><select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="ciq-input" data-testid="document-category"><option value="">None</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={upload} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="document-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
